import type { TacticApiClient } from "@gettactic/api";
import { queryOptions, useQueries, useQuery } from "@tanstack/react-query";
import { useTacticHooks } from "../../providers/TacticHooksProvider";

export interface UseMapSchedulableOptions {
  officeId: string | null;
  parentId: string | null;
  startDate: string | null;
  endDate?: string | null;
  enabled?: boolean;
  tags?: string[];
  tags_operator?: "AND" | "OR";
  organizerId?: string | null;
}

/**
 * Query definition shared by every schedulable-resources consumer, so a form
 * asking for the same office/floor/window as the map reads the cache entry
 * the map already filled instead of firing a second identical request.
 */
export const mapSchedulableQueryOptions = (
  api: TacticApiClient,
  options: UseMapSchedulableOptions
) => {
  const {
    officeId,
    parentId,
    startDate: startDateWithTimeStr,
    endDate: endDateWithTimeStr,
    enabled = true,
    tags,
    tags_operator,
    organizerId
  } = options;

  // An empty tag list and an absent one produce the exact same request, so
  // they must produce the same cache entry
  const tagsKey = tags?.length ? tags : undefined;

  return queryOptions({
    queryKey: [
      "organizations.schedulable",
      officeId,
      parentId,
      startDateWithTimeStr,
      endDateWithTimeStr,
      tagsKey,
      tags_operator,
      organizerId || undefined
    ],

    queryFn: async () => {
      const types = [
        "desk",
        "workspace",
        "meeting_room",
        "parking_lot",
        "parking_space"
      ];
      const res = await api.client.organizations.getSchedulableResources({
        officeId: officeId ?? "",
        parentId,
        startDate: startDateWithTimeStr ?? undefined,
        endDate: endDateWithTimeStr,
        types,
        tags,
        tags_operator,
        organizerId
      });
      // A failed request must surface as a query error, not resolve as
      // "zero schedulable resources" — callers (useOfficeResources) treat
      // an empty schedulable list as authoritative and mark every
      // resource missing from it unavailable, so silently coalescing a
      // failure here would make one bad request read as the whole office
      // being unavailable instead of a fetch that needs retrying.
      if (!res.ok) {
        throw new Error("Failed to load schedulable resources");
      }
      return {
        available: res.result?.available || [],
        nonAvailable: res.result?.nonAvailable || []
      };
    },

    enabled: enabled && !!startDateWithTimeStr && !!officeId
  });
};

export function useMapSchedulable(options: UseMapSchedulableOptions) {
  const { api } = useTacticHooks();

  const {
    data,
    error,
    isLoading: loading,
    isPending,
    refetch
  } = useQuery(mapSchedulableQueryOptions(api, options));
  return {
    schedulable: data?.available || [],
    nonAvailable: data?.nonAvailable || [],
    error,
    loading,
    isPending,
    refetch
  };
}

/**
 * One schedulable query per option set (typically one per selected date).
 * Each entry shares its cache key with `useMapSchedulable`, so a window the
 * map or the resource list already loaded is not requested again.
 */
export function useMapSchedulableQueries(
  optionsList: UseMapSchedulableOptions[]
) {
  const { api } = useTacticHooks();

  return useQueries({
    queries: optionsList.map((options) =>
      mapSchedulableQueryOptions(api, options)
    ),
    combine: (results) => ({
      nonAvailable: results.flatMap(
        (result) => result.data?.nonAvailable ?? []
      ),
      isPending: results.some((result) => result.isPending)
    })
  });
}
